// School management service. CRUD for schools, used by system admin dashboard.
import { supabase } from '../config/supabase';
import { authFetch } from './apiClient';

export interface School {
  id: string;
  name: string;
  address?: string;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSchoolData {
  name: string;
  address?: string;
  contactEmail: string;
}

class SchoolManagementService {
  /**
   * Generate a temporary password (unused by backend flow but kept for compatibility)
   */
  generateTemporaryPassword(): string {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    // M22: Use crypto.getRandomValues instead of Math.random for secure generation
    const secureRandom = (max: number) => {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      return arr[0] % max;
    };

    // Ensure password has at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[secureRandom(26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[secureRandom(26)];
    password += '0123456789'[secureRandom(10)];
    password += '!@#$%^&*'[secureRandom(8)];

    for (let i = password.length; i < length; i++) {
      password += charset[secureRandom(charset.length)];
    }

    // Shuffle using Fisher-Yates with secure randomness
    const arr = password.split('');
    for (let i = arr.length - 1; i > 0; i--) {
      const j = secureRandom(i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
  }

  /**
   * Create a new school by calling the backend API. Backend is responsible for
   * creating the School record, creating the admin auth user (using service key),
   * and creating the User profile.
   */
  async createSchool(data: CreateSchoolData): Promise<{ 
    success: boolean; 
    school?: School; 
    adminEmail?: string;
    temporaryPassword?: string;
    message?: string;
    error?: string 
  }> {
    try {
      const resp = await authFetch('/schools', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      const result = await resp.json();

      if (!resp.ok) {
        console.error('Error creating school via backend API:', result);
        // Backend returns { error: { code, message } }
        const errorMessage = result.error?.message || result.error || 'Failed to create school via API';
        return { success: false, error: errorMessage };
      }

      return { success: true, school: result.school, adminEmail: result.adminEmail, temporaryPassword: result.temporaryPassword, message: result.message };
    } catch (error) {
      console.error('Exception creating school:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Send welcome email to school admin (frontend fallback / logging)
   */
  async sendSchoolWelcomeEmail(
    _email: string,
    _schoolName: string,
    _temporaryPassword: string
  ): Promise<void> {
    // Welcome email is sent server-side by the backend on school creation.
    // This client-side stub is kept for interface compatibility.
  }

  /**
   * Get all schools (read-only via Supabase anon key) with backend fallback
   */
  async getAllSchools(): Promise<{ schools?: School[]; error?: string }> {
    try {
      // Try authenticated API first
      const resp = await authFetch('/schools');
      const result = await resp.json();
      
      if (!resp.ok) {
        // Fallback to Supabase direct read
        console.warn('Backend API failed, falling back to Supabase', result.error);
        if (!supabase) {
          return { error: 'Supabase is not configured' };
        }
        const { data: schools, error } = await supabase
          .from('School')
          .select('*')
          .order('createdAt', { ascending: false });

        if (error || !schools) {
          return { error: error?.message || 'Failed to fetch schools' };
        }
        return { schools };
      }
      
      return { schools: result.data || result.schools || [] };
    } catch (error) {
      return { 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }
}

export const schoolManagementService = new SchoolManagementService();

